import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import '../../assets/css/timeline.css';
import * as Utils from '../utils/Utils.js';
import LocaleFormat from '../utils/LocaleFormat.js';
import Loading from '../../components/ui/Loading.js';

/**
 * Resuelve la etiqueta de un campo: devuelve `{ mensaje }` para traducir, o `{ crudo }` cuando el
 * campo no está en el mapa (mostrarlo crudo es mejor que ocultarlo). Se exporta aparte del
 * componente para poder testear el mapeo sin renderizar.
 */
export const descriptorDeCampo = (campos, campo) => {
  const config = campos && campos[campo] ? campos[campo] : null;
  if (!config) return { crudo: campo ? campo : '' };
  return { mensaje: { id: config.labelId, defaultMessage: config.labelDefault } };
};

/**
 * Normaliza las formas en que puede venir un booleano. Los ABM legacy escriben el mismo campo
 * como 1 / '1' / true / 'true' (y el `false` a veces como cadena vacía), así que el diccionario
 * de `valores` se declara una sola vez con las claves '0' y '1'.
 */
export const claveDeValor = (valor) => {
  if (valor === true || valor === 1 || valor === '1' || valor === 'true') return '1';
  if (valor === false || valor === 0 || valor === '0' || valor === 'false') return '0';
  return String(valor);
};

/**
 * Ídem para un valor. Vacío/null se muestra como guion; un valor mapeado devuelve `{ mensaje }`;
 * un campo con `formato: 'numero'` devuelve `{ numero }` para que el componente lo formatee con
 * el locale; cualquier otro va crudo.
 */
export const descriptorDeValor = (campos, campo, valor) => {
  if (valor === null || valor === undefined || valor === '') return { crudo: '-' };

  const config = campos && campos[campo] ? campos[campo] : null;
  if (!config) return { crudo: String(valor) };

  if (config.valores) {
    const mapeado = config.valores[claveDeValor(valor)] || config.valores[String(valor)];
    if (mapeado) return { mensaje: mapeado };
  }

  if (config.formato === 'numero') {
    const numero = parseFloat(valor);
    if (!isNaN(numero)) return { numero: numero };
  }

  return { crudo: String(valor) };
};

/**
 * Icono y realce del badge de la fila. Un campo puede pedir el suyo (`icono`, `destacado`) —
 * como hace el histórico de costos, que marca los cambios de costo con `la-usd` y
 * `status-change` — y si no, va el icono de historial.
 */
export const badgeDeCampo = (campos, campo) => {
  const config = campos && campos[campo] ? campos[campo] : null;
  return {
    icono: config && config.icono ? config.icono : 'la la-history',
    destacado: !!(config && config.destacado),
  };
};


/**
 * Modal de "historial de cambios simple" para grillas.
 *
 * Es el diseño canónico del histórico campo/valor-anterior/valor-nuevo: el mismo que usa el
 * histórico de costos de Administración de ítems (`components/repuestos/RepuestoHistorico.jsx`
 * dentro de `#repuesto_historico_modal` de `RepuestosGrid.jsx`), extraído en VEC-3082 al
 * necesitarlo el historial de Servicios.
 *
 * Markup de modal Bootstrap (`modal-xl` = 1140px, alineado arriba con `margin: 1.75rem auto`) y
 * NO un `Dialog` de Material-UI: MUI centra vertical y su `paperFullWidth` resta 64px al ancho,
 * así que el mismo contenido quedaba más angosto y más abajo que el original.
 *
 * ## Contrato con el backend
 * `endpoint` tiene que devolver un array (más reciente primero) de:
 * `{ campo, valorAnterior, valorNuevo, usuario, fechaHora }`, donde `campo` es el nombre de la
 * COLUMNA y `usuario` ya viene armado como "Nombre Apellido" (null = lo hizo el sistema).
 * `exportEndpoint` (opcional) responde un .xlsx con el filename en `Content-Disposition`.
 *
 * ## Uso
 * ```jsx
 * <GridHistoryModal
 *   modalId="servicio_historial_modal"
 *   entidad={servicioSeleccionado}                                  // { id, nombre }
 *   titulo={{ id: '…', defaultMessage: ' Historial del Servicio: ' }}
 *   endpoint={(id) => `/servicios/${id}/historial`}
 *   exportEndpoint={(id) => `/servicios/${id}/historial/exportar-excel`}
 *   campos={CAMPOS}
 *   onClose={cerrar}
 * />
 * ```
 * La grilla lo abre con `$('#<modalId>').modal('show')` y conviene remontarlo con una `key` por
 * apertura, para que vuelva a pedir el historial (igual que `RepuestosGrid.handleVerHistorico`).
 *
 * ## `campos`
 * Mapa `{ [columna]: { labelId, labelDefault, valores?, formato?, icono?, destacado? } }`.
 * - `valores` mapea el valor a un descriptor de `intl.formatMessage`; se declara con las claves
 *   `'0'` / `'1'` y tolera las variantes con que los ABM legacy guardan un booleano
 *   (`1` / `'1'` / `true` / `'true'`).
 * - `formato: 'numero'` formatea con el locale del usuario (costos, importes).
 * - `icono` y `destacado` cambian el badge de esa fila del timeline — así el histórico de costos
 *   puede conservar su `la la-usd` y su clase `status-change`.
 * Un campo o un valor no mapeado se muestra crudo, que es mejor que ocultarlo.
 *
 * ## `mapFila`
 * Opcional. Función que normaliza cada fila cruda al shape de arriba, para endpoints que
 * devuelven la fila de la tabla tal cual y cuyo contrato no conviene cambiar:
 * ```js
 * mapFila={(f) => ({
 *   campo: f.campo, valorAnterior: f.valor_anterior, valorNuevo: f.valor_nuevo,
 *   usuario: f.per_nombre ? f.per_nombre + ' ' + f.per_apellido : null, fechaHora: f.fecha_hora,
 * })}
 * ```
 */
const GridHistoryModal = (props) => {
  const intl = useIntl();

  const [loading, setLoading] = useState(false);
  const [filas, setFilas] = useState([]);
  const [error, setError] = useState(false);
  const [exportando, setExportando] = useState(false);

  const entidad = props.entidad;
  const campos = props.campos ? props.campos : {};
  const modalId = props.modalId ? props.modalId : 'grid_history_modal';

  useEffect(() => {
    if (!entidad) {
      setFilas([]);
      setError(false);
      return;
    }
    setLoading(true);
    setError(false);
    Utils.getData(props.endpoint(entidad.id))
      .then((data) => {
        const crudas = Array.isArray(data) ? data : [];
        // `mapFila` permite consumir endpoints que devuelven la fila cruda de la tabla
        // (snake_case, usuario sin armar) sin cambiar su contrato, que puede tener otros clientes.
        setFilas(props.mapFila ? crudas.map(props.mapFila) : crudas);
      })
      .catch(() => {
        // Sin este flag un 403/500 se vería igual que "no hay cambios registrados".
        setFilas([]);
        setError(true);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entidad]);

  const nombreEntidad = () => {
    if (!entidad) return '';
    if (props.nombreEntidad) return props.nombreEntidad(entidad);
    if (entidad.nombre) return entidad.nombre;
    if (entidad.descripcion) return entidad.descripcion;
    return '';
  };

  const exportarExcel = () => {
    if (!entidad || !props.exportEndpoint || exportando || filas.length === 0) return;

    setExportando(true);
    Utils.dataBlob(props.exportEndpoint(entidad.id), 'POST', {})
      .then((res) => {
        const filename = res && res.filename ? res.filename.replace(/"/g, '') : 'fleet-historial.xlsx';
        const fileUrl = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = fileUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(fileUrl), 0);
      })
      .catch(() => {})
      .finally(() => setExportando(false));
  };

  const texto = (descriptor) => {
    if (descriptor.crudo !== undefined) return descriptor.crudo;
    if (descriptor.numero !== undefined) return descriptor.numero.toLocaleString(LocaleFormat.getNumberLocale());
    return intl.formatMessage(descriptor.mensaje);
  };
  const labelDeCampo = (campo) => texto(descriptorDeCampo(campos, campo));
  const formatValor = (campo, valor) => texto(descriptorDeValor(campos, campo, valor));

  const usuarioDe = (fila) =>
    fila.usuario
      ? fila.usuario
      : intl.formatMessage({
          id: 'gridHistoryModal.usuario_sistema',
          defaultMessage: 'Generado por el sistema',
        });

  /** Card del timeline. Se reusa para las filas, el vacío y el error, así ninguno desentona. */
  const renderCard = (titulo, detalle) => (
    <div className="timeline-card card border-grey border-lighten-2 card-history">
      <div className="card-header">
        <h4 className="card-title">{titulo}</h4>
        {detalle && (
          <p className="card-subtitle text-muted pt-1">
            <span className="font-small-3">{detalle}</span>
          </p>
        )}
      </div>
    </div>
  );

  const mensajeSinCambios = props.mensajeSinCambios
    ? props.mensajeSinCambios
    : { id: 'gridHistoryModal.sin_cambios', defaultMessage: 'No hubo cambios registrados.' };

  return (
    <div
      className="modal fade"
      tabIndex="-1"
      id={modalId}
      role="dialog"
      aria-labelledby={modalId + '_label'}
    >
      <div className="modal-xl modal-dialog" role="document">
        <div className="modal-content">
          <div className="modal-header bg-fleet">
            <h4 className="modal-title text-white" id={modalId + '_label'}>
              {/* El icono "flaquito" (Line Awesome) es el del header en todos los históricos. */}
              <i className="la la-history align-middle icon-modal-title"></i>
              <FormattedMessage {...props.titulo} />
              {nombreEntidad()}
            </h4>
            <button
              type="button"
              className="close"
              data-dismiss="modal"
              aria-label="Close"
              onClick={props.onClose}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-body modal-grey modal-mh ovf-x-hidden">
            {loading && <Loading />}
            <div className="content-wrapper">
              <div className="content-body">
                <section id="timeline" className="timeline-left timeline-wrapper">
                  <ul className="timeline">
                    <li className="timeline-line"></li>

                    {!loading && error && (
                      <li className="timeline-item">
                        {renderCard(
                          intl.formatMessage({
                            id: 'gridHistoryModal.error',
                            defaultMessage: 'No se pudo obtener el historial.',
                          }),
                          null
                        )}
                      </li>
                    )}

                    {!loading && !error && filas.length === 0 && (
                      <li className="timeline-item">{renderCard(intl.formatMessage(mensajeSinCambios), null)}</li>
                    )}

                    {filas.map((fila, indice) => {
                      const campoLabel = labelDeCampo(fila.campo);

                      // formatDateTime y no formatDate: sin la hora, dos cambios del mismo día
                      // quedarían indistinguibles.
                      const fecha = fila.fechaHora ? LocaleFormat.formatDateTime(fila.fechaHora) : '-';

                      const titulo =
                        fecha +
                        ' - ' +
                        intl.formatMessage(
                          { id: 'gridHistoryModal.evento_cambio', defaultMessage: 'Cambio de {campo}' },
                          { campo: campoLabel }
                        ) +
                        ' - ' +
                        usuarioDe(fila);

                      const detalle = intl.formatMessage(
                        { id: 'gridHistoryModal.detalle', defaultMessage: '{campo}: {anterior} → {nuevo}' },
                        {
                          campo: campoLabel,
                          anterior: formatValor(fila.campo, fila.valorAnterior),
                          nuevo: formatValor(fila.campo, fila.valorNuevo),
                        }
                      );

                      const badge = badgeDeCampo(campos, fila.campo);

                      return (
                        <li
                          key={'historial_' + indice}
                          className={badge.destacado ? 'timeline-item status-change' : 'timeline-item'}
                        >
                          <div className="timeline-badge">
                            <span className="bg-fleet bg-lighten-1" data-toggle="tooltip" data-placement="right">
                              <i className={badge.icono}></i>
                            </span>
                          </div>
                          {renderCard(titulo, detalle)}
                        </li>
                      );
                    })}
                  </ul>
                </section>
              </div>
            </div>
          </div>
          <div className="modal-footer modal-grey">
            {props.exportEndpoint && filas.length > 0 && (
              <button
                type="button"
                className="btn btn-default btn-fleet"
                onClick={exportarExcel}
                disabled={exportando || !entidad}
              >
                <i className={exportando ? 'la la-spinner spinner' : 'fa fa-download'}></i>
                <FormattedMessage id="gridHistoryModal.exportar_excel" defaultMessage=" Exportar a Excel" />
              </button>
            )}
            <button
              type="button"
              className="btn btn-default btn-fleet"
              data-dismiss="modal"
              onClick={props.onClose}
            >
              <FormattedMessage id="Ok" defaultMessage="Ok" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GridHistoryModal;
